// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canSubmitForApprovalOrder,
    getCurrentCartState
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";

import {
    CHECKOUT_STEPS,
    CheckoutStep,
    generateSteps,
    some,
} from "@steiner/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { doScroll } from "@steiner/XDL/Utilities";
import React, { createContext, PropsWithChildren, useRef, useState } from "react";

export interface StepperContextData {
    steps: CheckoutStep[];
    currentStep: number;
    maxStep: number;
    setCurrentStep: (step: number) => void;
    moveToStepHandler: (step: number) => void;
    setMaxStep: (step: number) => void;
    preventStepperChange: (hook: any, position: number) => void;
}

export type HasStepperContext = Pick<StepperContextData, "currentStep">;

const stepperContextData: StepperContextData = {
    steps: [],
    currentStep: 1,
    maxStep: 1,
    setCurrentStep: (step: number) => {},
    moveToStepHandler: (step: number) => {},
    setMaxStep: (step: number) => {},
    preventStepperChange: (hook: any, position: number) => {},
};

export const StepperContext = createContext<StepperContextData>(stepperContextData);

const StepperContextProvider: React.FC<
    PropsWithChildren<{
        initialStep?: number;
        handleFormSubmit?: (event?: React.FormEvent<HTMLFormElement>) => Promise<void>;
    }>
> = ({ initialStep = 1, children, handleFormSubmit }) => {
    const history = useHistory();
    const { checkoutShippingPage, isPickUp, cartPage, requiresApproval } = useAppSelector(state => ({
        checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
        cartPage: getPageLinkByPageType(state, "CartPage"),
        isPickUp: getSession(state).fulfillmentMethod === FulfillmentMethod.PickUp,
        requiresApproval: canSubmitForApprovalOrder(getCurrentCartState(state).value),
    }));
    const steps = generateSteps(isPickUp, requiresApproval);
    const [currentStep, doSetCurrentStep] = useState<number>(initialStep);
    const [maxStep, doSetMaxStep] = useState<number>(initialStep);
    const validationHookRef = useRef<any[]>([]);

    React.useEffect(() => {
        doSetCurrentStep(initialStep);
        doSetMaxStep(initialStep);
    }, [initialStep]);

    const setMaxStep = (state: number) => {
        doSetMaxStep(state);
    };

    // To update currentStep value to nextStep step
    const setCurrentStep = async (nextStep: number) => {
        const prevent = await some(validationHookRef.current, (item: any) => {
            const hook = item.hook.current(currentStep, nextStep);

            // check is already a promise and return
            if (typeof hook?.then === "function") {
                return hook;
            }
            return Promise.resolve(!!hook);
        });

        if (prevent) {
            return;
        }

        if (currentStep > maxStep) {
            setMaxStep(currentStep);
        }
        doSetCurrentStep(nextStep);
    };

    const preventStepperChange = (hook: any, position = 1) => {
        const vh = validationHookRef.current;
        const index = vh.findIndex((f: any) => f.position === position);
        if (index !== -1) {
            vh.splice(index, 1);
        }
        vh.push({ hook, position });
        vh.sort((a, b) => a.position - b.position);
    };

    const buildUrlWithCurrentSearch = (baseUrl?: string) => {
        if (!baseUrl) {
            return "";
        }
        const currentSearch = window.location.search || "";
        if (!currentSearch) {
            return baseUrl;
        }
        const [pathname] = baseUrl.split("?");
        return `${pathname}${currentSearch}`;
    };

    const pushIfDifferent = (targetUrl: string) => {
        if (!targetUrl) {
            return;
        }
        const currentUrl = `${window.location.pathname}${window.location.search}`;
        if (targetUrl !== currentUrl) {
            history.push(targetUrl);
        }
    };

    // To move to any step
    const moveToStep = (moveToNewStep: number) => {
        if (moveToNewStep === CHECKOUT_STEPS.CONFIRMATION) {
            return;
        }
        if (moveToNewStep === CHECKOUT_STEPS.CART) {
            pushIfDifferent(buildUrlWithCurrentSearch(cartPage?.url));
        } else if (moveToNewStep === CHECKOUT_STEPS.ADDRESS) {
            pushIfDifferent(buildUrlWithCurrentSearch(checkoutShippingPage?.url));

            setCurrentStep?.(moveToNewStep);
        } else {
            handleFormSubmit?.();
            doScroll(true);
        }
    };

    return (
        <StepperContext.Provider
            value={{
                steps,
                currentStep,
                setCurrentStep,
                maxStep,
                setMaxStep,
                preventStepperChange,
                moveToStepHandler: moveToStep,
            }}
        >
            {children}
        </StepperContext.Provider>
    );
};

export default StepperContextProvider;

export function withStepperContext<P extends HasStepperContext>(Component: React.ComponentType<P>) {
    return function StepperComponent(props: Omit<P, keyof HasStepperContext>) {
        return (
            <StepperContext.Consumer>
                {value => <Component {...(props as P)} currentStep={value.currentStep} />}
            </StepperContext.Consumer>
        );
    };
}
